import React from 'react';
import { Spacer, VStack } from '@chakra-ui/react';

import { BankAccount } from '__generated__/GQL';
import {
  getBankAccountDisplayName,
  parseBankAccountIllustrationName,
} from 'common/bankAccount/utils';
import { formatAmount } from 'common/formatAmount';
import SunshineCard from 'components/_core/SunshineCard';
import SunshineIcon from 'components/_core/SunshineIcon';
import Text from 'components/_core/Text';
import BankAccountIcon from 'components/BankAccountIcon';

type BankAccoutRequiredFields = Partial<BankAccount> &
  Pick<
    BankAccount,
    | 'illustrationName'
    | 'currency'
    | 'nextBalance'
    | 'bankAccountId'
    | 'name'
    | 'type'
  >;

interface BankAccountRowProps {
  bankAccount: BankAccoutRequiredFields;
  isDisabled?: boolean;
  onClick: () => void;
}

export const BankAccountRow: React.FC<BankAccountRowProps> = ({
  bankAccount,
  isDisabled = false,
  onClick,
}) => {
  const illustrationName = parseBankAccountIllustrationName(
    bankAccount.illustrationName,
  );
  const amount = formatAmount({
    currency: bankAccount.currency,
    forcePlusSign: false,
    value: bankAccount.nextBalance / 100,
  });
  return (
    <SunshineCard
      isDisabled={isDisabled}
      key={bankAccount.bankAccountId}
      onClick={onClick}
    >
      <SunshineCard.Content flexDirection="row" flexGrow={1} spacing="space-12">
        <BankAccountIcon
          illustrationName={illustrationName}
          isDisabled={isDisabled}
          size="md"
        />
        <VStack align="flex-start" spacing={0}>
          <Text
            fontSize="font-15"
            fontWeight="weight-500"
            noOfLines={1}
            variant={isDisabled ? 'disabled' : 'primary'}
          >
            {getBankAccountDisplayName({
              name: bankAccount.name,
              type: bankAccount.type,
            })}
          </Text>
          <Text
            fontSize="font-13"
            fontWeight="weight-400"
            variant={isDisabled ? 'disabled' : 'primary'}
          >
            {amount}
          </Text>
        </VStack>
        <Spacer />
        <SunshineIcon
          color={isDisabled ? 'grey.500' : 'grey.600'}
          name="chevron-right"
          size="icon-24"
        />
      </SunshineCard.Content>
    </SunshineCard>
  );
};
